import { useEffect, useRef, useState } from 'react';
import {
  db, enqueue, setting, setSetting, wipeLocal, pendingCount, seedTags,
  type LocalExhibition, type LocalSupplier, type LocalStone,
} from './db';
import { onSync, setApiToken, apiFetch, syncNow, deviceCode, type SyncState } from './sync';
import { SupplierView } from './screens/Catalog';
import SupplierEdit from './screens/SupplierEdit';
import { TabBar, Today, Suppliers, More, type Tab } from './screens/Shell';
import { loadTheme } from './lib/theme';
import { Browse, Compare } from './screens/Browse';
import CardScan from './screens/CardScan';
import Capture from './screens/Capture';
import StoneView from './screens/StoneView';
import Access from './screens/Access';
import CertScan from './screens/CertScan';
import Tags from './screens/Tags';
import Collections from './screens/Collections';
import Trash from './screens/Trash';
import { Storefront, Orders } from './screens/Shop';
import Desk from './screens/Desk';
import Interpret from './screens/Interpret';
import { TalkView, UnlinkedTalks } from './screens/Talks';
import Tasks from './screens/Tasks';
import Assistant from './screens/Assistant';
import {
  ulid, plural, normalizePhone, looksLikePhone, placeTitle, placeCode, currencyForPlace,
  type Filter, type PlaceKind, type PlaceLike,
} from '@core';
import { frameFromFile } from './lib/media';

/**
 * Экраны двух родов: вкладки, между которыми ходят нижним меню, и то, что
 * открывается поверх — карточка, камера, настройка. Вкладка запоминается,
 * поэтому возврат из карточки приводит туда, откуда ушли.
 */
type Overlay =
  | { name: 'card' }
  | { name: 'supplier'; id: string }
  | { name: 'supplierEdit'; id: string }
  | { name: 'capture'; supplierId: string; sessionId: string; kind: 'stone' | 'parcel' }
  | { name: 'stone'; id: string }
  | { name: 'cert'; stoneId: string }
  | { name: 'shows' }
  | { name: 'device' }
  | { name: 'access' }
  | { name: 'compare'; ids: string[] }
  | { name: 'tags' }
  | { name: 'collections' }
  | { name: 'trash' }
  | { name: 'storefront' }
  | { name: 'orders' }
  | { name: 'talk'; supplierId?: string }
  | { name: 'talkView'; id: string }
  | { name: 'talks' }
  | { name: 'tasks' }
  | { name: 'assistant' };

interface Me {
  name?: string; phone?: string; role?: string;
  login_quota?: number; workspace_name?: string;
}

export default function App() {
  const [tab, setTab] = useState<Tab>('today');

  // Стек, а не один экран: из камня, открытого из вкладки «Камни», назад
  // должно возвращать в неё, а не к поставщику, которого смотрели полчаса
  // назад. Нижнее меню при этом остаётся на месте — уйти домой можно всегда.
  const [stack, setStack] = useState<Overlay[]>([]);
  const over = stack.length ? stack[stack.length - 1] : null;
  const push = (o: Overlay) => setStack((s) => [...s, o]);
  const back = () => setStack((s) => s.slice(0, -1));
  const goTab = (t: Tab) => { setStack([]); setTab(t); };
  // Заход в каталог нижним меню — это «покажи всё», а не повтор прошлого
  // вопроса. Отбор с главного экрана живёт ровно один переход.
  const openTab = (t: Tab) => { setCatalogFilter(undefined); goTab(t); };
  const [exhibition, setExhibition] = useState<LocalExhibition | null>(null);
  const [supplier, setSupplier] = useState<LocalSupplier | null>(null);
  const [me, setMe] = useState<Me | null>(null);
  const [sync, setSync] = useState<SyncState>({ pending: 0, online: true, running: false, unauthorized: false });
  const [keySkipped, setKeySkipped] = useState(false);
  const [logoutProblem, setLogoutProblem] = useState('');
  // С главного экрана в каталог заходят уже с вопросом — отбор передаётся туда.
  const [catalogFilter, setCatalogFilter] = useState<Filter | undefined>(undefined);
  const [ready, setReady] = useState(false);

  // Кокпит: на широком экране (iPad, ноутбук) вместо телефонной раскладки —
  // рабочий стол с разделами. Выключается кнопкой и запоминается.
  const [wide, setWide] = useState(() => window.matchMedia('(min-width: 820px) and (min-height: 500px)').matches);
  const [deskOff, setDeskOff] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 820px) and (min-height: 500px)');
    const fn = (e: MediaQueryListEvent) => setWide(e.matches);
    mq.addEventListener('change', fn);
    return () => mq.removeEventListener('change', fn);
  }, []);
  const desk = ready && wide && !deskOff && !sync.unauthorized;

  useEffect(() => onSync(setSync), []);

  /*
   * Системная кнопка «назад» на Android.
   *
   * Приложение живёт на одной странице, истории у браузера нет — и кнопка
   * возврата выбрасывала из него целиком, посреди работы у стенда. Держим
   * в истории одну лишнюю запись: пока есть что свернуть — карточка, камера,
   * настройка, — «назад» сворачивает её, и запись возвращается на место.
   * Когда сворачивать нечего, запись не восстанавливаем, и следующее нажатие
   * честно выходит из приложения.
   */
  const nav = useRef({ stack, tab });
  nav.current = { stack, tab };
  useEffect(() => {
    const onPop = () => {
      const { stack: st, tab: t } = nav.current;
      const top = st.length ? st[st.length - 1] : null;
      if (top?.name === 'capture') {
        // Съёмку нельзя просто закрыть: заход к поставщику надо завершить,
        // иначе он останется открытым навсегда.
        void closeCapture(top.supplierId, top.sessionId);
        history.pushState(null, '');
      } else if (st.length) {
        setStack((prev) => prev.slice(0, -1));
        history.pushState(null, '');
      } else if (t !== 'today') {
        setStack([]);
        setTab('today');
        history.pushState(null, '');
      }
    };
    history.pushState(null, '');
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  useEffect(() => {
    void (async () => {
      const id = await setting<string>('exhibitionId', '');
      let picked = id ? (await db.exhibitions.get(id)) ?? null : null;

      // Выставка в каталоге одна — выбирать её вручную незачем. Лишнее
      // действие на первом же экране, и всегда с одним исходом.
      if (!picked) {
        const all = await db.exhibitions.toArray();
        if (all.length === 1) {
          picked = all[0];
          await setSetting('exhibitionId', picked.id);
        }
      }
      setExhibition(picked);
      // Словарь видов нужен на съёмке, а завести его надо заранее: у стенда
      // не место ждать, пока он разложится.
      void seedTags();
      setDeskOff(await setting<boolean>('deskOff', false));
      await loadTheme();
      setReady(true);
    })();
  }, []);

  // Кто вошёл — нужно и для приветствия, и чтобы раздел доступа видел
  // только владелец каталога.
  useEffect(() => {
    if (sync.unauthorized) { setMe(null); return; }
    void (async () => {
      try {
        const r = await apiFetch('/api/me');
        if (!r.ok) return;
        const who = await r.json();
        setMe(who);

        // На одном телефоне могут работать разные люди. Если ключ теперь
        // принадлежит другому, чужой каталог с устройства убирается: сервер
        // его уже не отдаёт, а локальная база иначе показала бы прежнего
        // владельца следующему.
        const seen = await setting<string>('userId', '');
        if (!who?.id) return;

        // Владелец сменился — чужое с устройства убираем.
        let foreign = Boolean(seen) && seen !== who.id;

        // Отметки владельца нет вовсе, а каталог на устройстве есть. Приписать
        // его некому: он мог остаться от прежнего человека. Стираем только
        // когда очередь пуста — тогда всё снятое уже на сервере, и своё
        // вернётся следующей же синхронизацией.
        if (!seen && !foreign) {
          const [own, left] = await Promise.all([db.stones.count(), pendingCount()]);
          const shows = await db.exhibitions.count();
          foreign = left === 0 && (own > 0 || shows > 0);
        }

        if (foreign) {
          await wipeLocal();
          setExhibition(null);
          setStack([]);
        }
        if (seen !== who.id) await setSetting('userId', who.id);
        if (foreign) void syncNow();
      } catch { /* оффлайн — обойдёмся без имени */ }
    })();
  }, [sync.unauthorized]);

  useEffect(() => {
    const id = over?.name === 'capture' ? over.supplierId
             : over?.name === 'supplier' ? over.id : null;
    if (id) void db.suppliers.get(id).then((s) => setSupplier(s ?? null));
  }, [over]);

  async function openCapture(supplierId: string, kind: 'stone' | 'parcel') {
    if (!exhibition) return;
    const sessionId = ulid();
    const startedAt = new Date().toISOString();
    await db.sessions.put({ id: sessionId, exhibitionId: exhibition.id, supplierId, startedAt });
    await enqueue('session.upsert', { id: sessionId, exhibitionId: exhibition.id, supplierId, startedAt }, ulid());
    push({ name: 'capture', supplierId, sessionId, kind });
  }

  /** Уйти из съёмки в раздел меню: заход закрываем, стек сбрасываем. */
  async function leaveCapture(supplierId: string, sessionId: string, t: Tab) {
    const endedAt = new Date().toISOString();
    await db.sessions.update(sessionId, { endedAt });
    await enqueue('session.upsert', { id: sessionId, endedAt }, ulid());
    setStack([]);
    setCatalogFilter(undefined);
    setTab(t);
    void syncNow();
  }

  async function closeCapture(supplierId: string, sessionId: string) {
    const endedAt = new Date().toISOString();
    await db.sessions.update(sessionId, { endedAt });
    await enqueue('session.upsert', { id: sessionId, endedAt }, ulid());
    // Из камеры выходим к поставщику, а не обратно в камеру.
    setStack((s) => [...s.filter((o) => o.name !== 'capture'),
      ...(s.some((o) => o.name === 'supplier') ? [] : [{ name: 'supplier' as const, id: supplierId }])]);
    void syncNow();
  }

  // Камера занимает экран целиком — там меню мешало бы съёмке. Везде
  // остальном оно на месте: с любого экрана видно, где ты и куда уйти.
  const fullscreen = over?.name === 'capture' || over?.name === 'card' || over?.name === 'cert' || over?.name === 'talk'
    || over?.name === 'assistant';
  useEffect(() => {
    document.body.classList.toggle('has-tabs', ready && !sync.unauthorized && !fullscreen && !desk);
  }, [ready, sync.unauthorized, fullscreen, desk]);

  if (!ready) return <div className="app"><div className="blankslate">Загружаю…</div></div>;

  if (sync.unauthorized && !keySkipped) {
    return <PhoneGate onSkip={() => setKeySkipped(true)} />;
  }

  if (desk) {
    return (
      <>
        <Desk
          me={me} exhibition={exhibition}
          onPhoneView={() => { setDeskOff(true); void setSetting('deskOff', true); }}
        />
        <SyncPill state={sync} />
      </>
    );
  }

  // ── то, что открывается поверх вкладок ──────────────────────────────────

  if (over?.name === 'card' && exhibition) {
    return (
      <CardScan
        exhibition={exhibition}
        onDone={(id) => void openCapture(id, 'stone')}
        onCancel={back}
        onPlaceUpdated={setExhibition}
      />
    );
  }

  /*
   * Съёмка не размонтируется, пока идёт заход к стенду.
   *
   * Поверх неё открываются сертификат и карточка камня, а черновик кадра —
   * выбранные виды и поправленный текст заметки — остаётся на месте. Если
   * убрать её из дерева, всё это пропадёт, и человек, сходивший за
   * сертификатом, вернётся к пустому экрану.
   */
  const capturing = stack.find((o) => o.name === 'capture');
  if (capturing?.name === 'capture' && supplier && exhibition) {
    return (
      <>
        <Capture
          supplier={supplier} exhibition={exhibition}
          sessionId={capturing.sessionId} kind={capturing.kind}
          onClose={() => void closeCapture(capturing.supplierId, capturing.sessionId)}
          onOpenStone={(id) => push({ name: 'stone', id })}
          onScanCert={(id) => push({ name: 'cert', stoneId: id })}
        />
        {over?.name === 'cert' && (
          <div className="layer">
            <CertOverlay stoneId={over.stoneId} onDone={back} onCancel={back} />
          </div>
        )}
        {over?.name === 'stone' && (
          <div className="layer">
            <StoneView
              stoneId={over.id} onBack={back}
              backLabel="К съёмке"
              onScanCert={(st) => push({ name: 'cert', stoneId: st.id })}
              onOpenStone={(id) => push({ name: 'stone', id })}
            />
          </div>
        )}
        {/*
          * Нижнее меню на карточке есть и здесь.
          *
          * Камера занимает экран целиком — там меню мешало бы съёмке. Но
          * карточка, открытая поверх неё, это обычный экран, и человек ищет
          * на нём привычные кнопки. Не найдя их, он оказывается в тупике:
          * единственный выход — стрелка вверху, а её ещё надо заметить.
          *
          * Уход по кнопке завершает заход к поставщику, иначе он остался бы
          * открытым навсегда.
          */}
        {over?.name === 'stone' && (
          // Карточка лежит слоем выше камеры, поэтому меню надо поднять над
          // ней — иначе кнопки окажутся под карточкой и их просто не видно.
          <div className="overcam">
            <TabBar offices={exhibition?.kind === 'building'} tab={tab} onTab={(t) => void leaveCapture(capturing.supplierId, capturing.sessionId, t)} />
          </div>
        )}
        <SyncPill state={sync} />
      </>
    );
  }

  if (over?.name === 'supplier') {
    return (
      <>
        <SupplierView
          supplierId={over.id}
          onBack={back}
          onCapture={(kind) => void openCapture(over.id, kind)}
          onOpenStone={(id) => push({ name: 'stone', id })}
          onEdit={() => push({ name: 'supplierEdit', id: over.id })}
          onMerged={(target) => setStack((st) => [...st.slice(0, -1), { name: 'supplier', id: target }])}
          onTalk={() => push({ name: 'talk', supplierId: over.id })}
          onOpenTalk={(id) => push({ name: 'talkView', id })}
        />
        <TabBar offices={exhibition?.kind === 'building'} tab={tab} onTab={openTab} />
        <SyncPill state={sync} />
      </>
    );
  }

  if (over?.name === 'supplierEdit') {
    return (
      <>
        <SupplierEdit supplierId={over.id} onBack={back} onSaved={back} />
        <SyncPill state={sync} />
      </>
    );
  }

  if (over?.name === 'stone') {
    return (
      <>
        <StoneView
          stoneId={over.id}
          onBack={back}
          onScanCert={(st) => push({ name: 'cert', stoneId: st.id })}
          onOpenStone={(id) => push({ name: 'stone', id })}
        />
        <TabBar offices={exhibition?.kind === 'building'} tab={tab} onTab={openTab} />
        <SyncPill state={sync} />
      </>
    );
  }

  if (over?.name === 'cert') {
    return <CertOverlay stoneId={over.stoneId} onDone={back} onCancel={back} />;
  }

  if (over?.name === 'shows') {
    return (
      <>
        <Shows
          current={exhibition}
          onBack={back}
          onPicked={(ex) => { setExhibition(ex); goTab('today'); }}
        />
        <TabBar offices={exhibition?.kind === 'building'} tab={tab} onTab={openTab} />
      </>
    );
  }

  if (over?.name === 'compare') {
    return (
      <>
        <Compare
          ids={over.ids}
          onBack={back}
          onOpenStone={(id) => push({ name: 'stone', id })}
        />
        <TabBar offices={exhibition?.kind === 'building'} tab={tab} onTab={openTab} />
      </>
    );
  }

  if (over?.name === 'access') {
    return <><Access onBack={back} /><TabBar offices={exhibition?.kind === 'building'} tab={tab} onTab={openTab} /></>;
  }
  if (over?.name === 'storefront') {
    return <><Storefront onBack={back} onOrders={() => push({ name: 'orders' })} />
      <TabBar offices={exhibition?.kind === 'building'} tab={tab} onTab={openTab} /></>;
  }
  if (over?.name === 'orders') {
    return <><Orders onBack={back} onOpenStone={(id) => push({ name: 'stone', id })} />
      <TabBar offices={exhibition?.kind === 'building'} tab={tab} onTab={openTab} /></>;
  }
  if (over?.name === 'trash') {
    return <><Trash onBack={back} /><TabBar offices={exhibition?.kind === 'building'} tab={tab} onTab={openTab} /></>;
  }
  if (over?.name === 'collections') {
    return <><Collections onBack={back} /><TabBar offices={exhibition?.kind === 'building'} tab={tab} onTab={openTab} /></>;
  }
  if (over?.name === 'tags') {
    return <><Tags onBack={back} /><TabBar offices={exhibition?.kind === 'building'} tab={tab} onTab={openTab} /></>;
  }
  if (over?.name === 'talk') {
    // Беседа занимает экран целиком: меню внизу оказалось бы на половине
    // поставщика, да ещё вверх ногами для него.
    return <Interpret exhibition={exhibition} supplierId={over.supplierId} onBack={back} />;
  }
  if (over?.name === 'talkView') {
    return <><TalkView talkId={over.id} onBack={back} />
      <TabBar offices={exhibition?.kind === 'building'} tab={tab} onTab={openTab} /></>;
  }
  if (over?.name === 'assistant') {
    // Поле ввода внизу — на месте нижнего меню, поэтому меню здесь нет.
    return <Assistant onBack={back} />;
  }
  if (over?.name === 'tasks') {
    return <><Tasks onBack={back} onOpenTalk={(id) => push({ name: 'talkView', id })} />
      <TabBar offices={exhibition?.kind === 'building'} tab={tab} onTab={openTab} /></>;
  }
  if (over?.name === 'talks') {
    return <><UnlinkedTalks onBack={back} onOpen={(id) => push({ name: 'talkView', id })} />
      <TabBar offices={exhibition?.kind === 'building'} tab={tab} onTab={openTab} /></>;
  }
  if (over?.name === 'device') {
    return <><Device onBack={back} /><TabBar offices={exhibition?.kind === 'building'} tab={tab} onTab={openTab} /></>;
  }

  // ── вкладки ─────────────────────────────────────────────────────────────

  return (
    <>
      {tab === 'today' && (
        <Today
          exhibition={exhibition} me={me}
          onPickShow={() => push({ name: 'shows' })}
          onScanCard={() => push({ name: 'card' })}
          onOpenSupplier={(id) => push({ name: 'supplier', id })}
          onOpenStone={(id) => push({ name: 'stone', id })}
          onBrowse={(f) => { setCatalogFilter(f); goTab('stones'); }}
          onShoot={(id) => void openCapture(id, 'stone')}
          onTalk={() => push({ name: 'talk' })}
          onUnlinkedTalks={() => push({ name: 'talks' })}
          onTasks={() => push({ name: 'tasks' })}
        />
      )}
      {tab === 'stones' && (
        <Browse
          initial={catalogFilter}
          onOpenStone={(id) => push({ name: 'stone', id })}
          onCompare={(ids) => push({ name: 'compare', ids })}
        />
      )}
      {tab === 'suppliers' && (
        <Suppliers offices={exhibition?.kind === 'building'} onOpenSupplier={(id) => push({ name: 'supplier', id })} />
      )}
      {tab === 'more' && (
        <More
          me={me} exhibition={exhibition}
          onDesk={wide ? () => { setDeskOff(false); void setSetting('deskOff', false); } : undefined}
          onPickShow={() => push({ name: 'shows' })}
          onAccess={() => push({ name: 'access' })}
          onSettings={() => push({ name: 'device' })}
          onTags={() => push({ name: 'tags' })}
          onCollections={() => push({ name: 'collections' })}
          onTrash={() => push({ name: 'trash' })}
          onShop={() => push({ name: 'storefront' })}
          onTalk={() => push({ name: 'talk' })}
          onTasks={() => push({ name: 'tasks' })}
          onAssistant={() => push({ name: 'assistant' })}
          logoutProblem={logoutProblem}
          onLogout={async () => {
            // Стереть каталог с непереданной съёмкой — потерять её насовсем.
            const left = await pendingCount();
            if (left > 0) {
              setLogoutProblem(
                `Не отправлено записей: ${left}. Дождитесь связи — иначе они пропадут вместе с выходом.`);
              return;
            }
            await wipeLocal();
            await setSetting('userId', '');
            await setApiToken('');
            setExhibition(null);
            setLogoutProblem('');
            setKeySkipped(false);
          }}
        />
      )}

      <TabBar offices={exhibition?.kind === 'building'} tab={tab} onTab={openTab} />
      <SyncPill state={sync} />
    </>
  );
}

/** Сертификату нужен сам камень, а он лежит в локальной базе. */
function CertOverlay({ stoneId, onDone, onCancel }: {
  stoneId: string; onDone: () => void; onCancel: () => void;
}) {
  const [stone, setStone] = useState<LocalStone | null>(null);
  useEffect(() => { void db.stones.get(stoneId).then((s) => setStone(s ?? null)); }, [stoneId]);
  if (!stone) return <div className="app"><div className="blankslate">Загружаю…</div></div>;
  return <CertScan stone={stone} onDone={onDone} onCancel={onCancel} />;
}

// ── состояние очереди ───────────────────────────────────────────────────────

function SyncPill({ state }: { state: SyncState }) {
  const [visible, setVisible] = useState(false);

  // Без сети плашка висит постоянно — это состояние, о котором надо знать.
  // С сетью показывается коротко и уходит: постоянный счётчик мешает читать.
  useEffect(() => {
    if (!state.online) { setVisible(true); return; }
    if (!state.pending) { setVisible(false); return; }
    setVisible(true);
    const t = setTimeout(() => setVisible(false), 3500);
    return () => clearTimeout(t);
  }, [state.online, state.pending, state.running]);

  if (!visible) return null;
  const n = state.pending;
  const text = !state.online
    ? `Без сети · ${n} ${plural(n, 'запись сохранена', 'записи сохранены', 'записей сохранено')}`
    : state.running
      ? `Отправляю… осталось ${n}`
      : `${n} ${plural(n, 'запись ждёт', 'записи ждут', 'записей ждут')} отправки`;

  return <div className={`pill${state.online ? '' : ' warn'}`}><span className="led" />{text}</div>;
}

// ── вход ────────────────────────────────────────────────────────────────────

function PhoneGate({ onSkip }: { onSkip: () => void }) {
  const [phone, setPhone] = useState('');
  const [checking, setChecking] = useState(false);
  const [problem, setProblem] = useState('');

  const ok = looksLikePhone(phone);

  async function enter() {
    if (!ok) return;
    setChecking(true);
    setProblem('');
    try {
      const res = await fetch('/api/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: normalizePhone(phone) }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setProblem(data?.error ?? 'Не получилось войти.'); return; }
      await setApiToken(data.key);
      void syncNow();
    } catch {
      setProblem('Нет связи с сервером. Проверьте интернет.');
    } finally {
      setChecking(false);
    }
  }

  return (
    <div className="app">
      <div className="bar">
        <h1 className="t">Ваш номер телефона</h1>
        <p className="meta">
          По нему приложение узнаёт, чей это каталог. Номер должен быть заранее
          подключён — тем, кто выдавал вам доступ.
        </p>
      </div>

      <div className="scroll">
        <label className="field">
          <span>Телефон</span>
          <input value={phone} onChange={(e) => { setPhone(e.target.value); setProblem(''); }}
                 onKeyDown={(e) => { if (e.key === 'Enter') void enter(); }}
                 placeholder="050-000-0000" inputMode="tel" autoComplete="tel" autoFocus />
          {phone && !ok && <span className="hint">Похоже, номер введён не полностью.</span>}
          {problem && <span className="hint" style={{ color: 'var(--alert)' }}>{problem}</span>}
        </label>

        <div className="memo">
          Снимать можно и не входя: камни, визитки и заметки лягут на телефон.
          Но распознавание визиток, расшифровка речи и общий каталог работают
          только после входа.
        </div>
      </div>

      <div className="dock">
        <button className="btn btn-secondary" onClick={onSkip}>Пока без входа</button>
        <button className="btn btn-primary" disabled={!ok || checking} onClick={() => void enter()}>
          {checking ? 'Проверяю…' : 'Войти'}
        </button>
      </div>
    </div>
  );
}

// ── выставки ────────────────────────────────────────────────────────────────

function Shows({ current, onBack, onPicked }: {
  current: LocalExhibition | null;
  onBack: () => void;
  onPicked: (ex: LocalExhibition) => void;
}) {
  const [list, setList] = useState<LocalExhibition[]>([]);
  const [adding, setAdding] = useState(false);
  /*
   * Выставка или здание. Выставка — как было: название, город, площадка.
   * Здание — то, что о нём известно: название, адрес или ориентир, любое
   * одно. В Гонконге покупатель ходит по офисным башням, и «напротив
   * нефритового рынка» — это уже достаточно, чтобы начать снимать.
   */
  const [kind, setKind] = useState<PlaceKind>('show');
  const [name, setName] = useState('');
  const [city, setCity] = useState('');
  const [venue, setVenue] = useState('');
  const [address, setAddress] = useState('');
  const [landmark, setLandmark] = useState('');
  const [currency, setCurrency] = useState('');
  const [reading, setReading] = useState(false);
  const [readProblem, setReadProblem] = useState('');
  const [doubtful, setDoubtful] = useState<Set<string>>(new Set());
  const shot = useRef<HTMLInputElement>(null);

  const draft: PlaceLike = { kind, name, address, landmark };
  const title = placeTitle(draft);
  // Обозначение выводится само: незачем требовать от человека придумывать
  // то, что система выведет из названия или адреса.
  const code = title ? placeCode(draft) : '';
  const ready = kind === 'show' ? Boolean(name.trim()) : Boolean(title);
  // Валюта выводится из города, пока человек не выбрал сам: Гонконг — HKD,
  // Бангкок — THB. Сказанное поставщиком вслух всегда важнее этого умолчания.
  const guessedCurrency = currencyForPlace({ city, address });
  const chosenCurrency = currency || guessedCurrency;
  const CURS = [guessedCurrency, ...['USD', 'HKD', 'THB', 'CNY', 'EUR', 'ILS'].filter((c) => c !== guessedCurrency)];

  useEffect(() => {
    void db.exhibitions.reverse().sortBy('createdAt').then((l) => {
      setList(l);
      if (!l.length) setAdding(true);
      // Город подставляем из последнего места: за день его не меняют.
      const last = current ?? l[0];
      if (last?.city) setCity(last.city);
      if (last?.kind === 'building') setKind('building');
    });
  }, []);

  /**
   * Табличка у входа, вывеска, указатель в лифте, визитка — всё, с чего можно
   * узнать, где ты. Снимок читает та же модель, что визитки, и заполняет поля;
   * человек только проверяет. Набирать адрес гонконгской башни на телефоне
   * у входа никто не станет.
   */
  async function readPlace(file: File) {
    setReading(true);
    setReadProblem('');
    try {
      const { preview } = await frameFromFile(file);
      const fd = new FormData();
      fd.append('file', preview, 'place.webp');
      const res = await apiFetch('/api/scan-place', { method: 'POST', body: fd });
      const d = await res.json().catch(() => ({} as any));
      if (!res.ok) throw new Error(d?.error ?? `сервер ответил ${res.status}`);
      const pl = d.place ?? {};
      const pick = (b: any) => (b?.latin || b?.original || '').trim();
      // Заполняем только пустое: то, что человек уже вписал, дороже прочтённого.
      if (!name.trim() && pick(pl.building)) setName(pick(pl.building));
      if (!address.trim() && pick(pl.address)) setAddress(pick(pl.address));
      if (!landmark.trim() && pl.landmark) setLandmark(String(pl.landmark));
      if (!city.trim() && pick(pl.city)) setCity(pick(pl.city));
      setDoubtful(new Set(pl.uncertain ?? []));
      if (!pick(pl.building) && !pick(pl.address) && !pl.landmark) {
        setReadProblem('На снимке не нашлось ни названия, ни адреса. Впишите, что знаете, — хватит одного.');
      }
    } catch (e: any) {
      setReadProblem(`${e?.message ?? e} Впишите, что знаете, — хватит одного.`);
    } finally {
      setReading(false);
    }
  }

  async function create() {
    if (!ready) return;
    const ex: LocalExhibition = {
      id: ulid(), code, kind,
      // У здания названия может не быть: его называют адресом или ориентиром.
      name: name.trim(),
      address: kind === 'building' ? address.trim() || undefined : undefined,
      landmark: kind === 'building' ? landmark.trim() || undefined : undefined,
      city: city || undefined, venue: kind === 'show' ? venue || undefined : undefined,
      currency: chosenCurrency,
      // День выставки считается от даты начала; у здания дней нет.
      startsOn: kind === 'show' ? new Date().toISOString().slice(0, 10) : undefined,
      createdAt: new Date().toISOString(),
    };
    await db.exhibitions.put(ex);
    await enqueue('exhibition.upsert', ex, ulid());
    await setSetting('exhibitionId', ex.id);
    void syncNow();
    onPicked(ex);
  }

  async function pick(ex: LocalExhibition) {
    await setSetting('exhibitionId', ex.id);
    onPicked(ex);
  }

  const kindLabel = (e: LocalExhibition) => (e.kind === 'building' ? 'здание' : 'выставка');

  return (
    <div className="app">
      <div className="bar">
        <button className="crumb" onClick={onBack}>‹ Назад</button>
        <h1 className="t">Где вы сегодня</h1>
        <p className="meta">Выставка или здание. Выбранное подставляется во всё, что вы снимаете.</p>
      </div>

      <div className="scroll">
        {list.length > 0 && (
          <div className="list" style={{ marginTop: 4 }}>
            {list.map((e) => (
              <button key={e.id} className="row" onClick={() => void pick(e)}>
                <span className="mark">{e.code.slice(0, 2)}</span>
                <span className="grow">
                  <span className="nm">{placeTitle(e) || e.code}</span>
                  <span className="sub">
                    {[kindLabel(e), e.kind === 'building' && e.name ? e.address : null, e.city, e.venue]
                      .filter(Boolean).join(' · ')}
                  </span>
                </span>
                {current?.id === e.id
                  ? <span className="src src-certified">сейчас</span>
                  : <span className="chev">›</span>}
              </button>
            ))}
          </div>
        )}

        {adding ? (
          <div className="memo" style={{ marginTop: 14 }}>
            <div className="states" style={{ marginTop: 0, marginBottom: 14 }}>
              <button className="state-btn" aria-pressed={kind === 'show'} onClick={() => setKind('show')}>
                Выставка
              </button>
              <button className="state-btn" aria-pressed={kind === 'building'} onClick={() => setKind('building')}>
                Здание
              </button>
            </div>

            {kind === 'show' ? (
              <>
                <label className="field">
                  <span>Название выставки</span>
                  <input value={name} onChange={(e) => setName(e.target.value)}
                         placeholder="Hong Kong Jewellery Show 2026" autoFocus />
                </label>
                <div className="pair">
                  <label className="field"><span>Город</span>
                    <input value={city} onChange={(e) => setCity(e.target.value)} placeholder="Гонконг" /></label>
                  <label className="field"><span>Площадка</span>
                    <input value={venue} onChange={(e) => setVenue(e.target.value)} placeholder="AsiaWorld-Expo" /></label>
                </div>
              </>
            ) : (
              <>
                {/* Сначала снимок, потом поля: у входа в башню табличка есть
                    почти всегда, а набирать адрес на телефоне — последнее дело. */}
                <input ref={shot} type="file" accept="image/*" capture="environment" hidden
                       onChange={(e) => {
                         const f = e.target.files?.[0];
                         e.target.value = '';
                         if (f) void readPlace(f);
                       }} />
                <button className="btn btn-secondary" disabled={reading}
                        onClick={() => shot.current?.click()}>
                  {reading ? 'Читаю снимок…' : '📷 Снять табличку или визитку — заполню сам'}
                </button>
                <p className="meta" style={{ margin: '8px 0 14px' }}>
                  Или впишите, что знаете. Хватит одного: названия, адреса или ориентира.
                </p>
                {readProblem && <div className="memo warn" style={{ marginTop: 0 }}>{readProblem}</div>}

                <label className="field">
                  <span>Название здания{doubtful.has('building') ? ' · проверьте' : ''}</span>
                  <input value={name} onChange={(e) => setName(e.target.value)}
                         placeholder="Jade Building" />
                </label>
                <label className="field">
                  <span>Адрес{doubtful.has('address') ? ' · проверьте' : ''}</span>
                  <input value={address} onChange={(e) => setAddress(e.target.value)}
                         placeholder="123 Nathan Road, Tsim Sha Tsui" />
                </label>
                <label className="field">
                  <span>Ориентир</span>
                  <input value={landmark} onChange={(e) => setLandmark(e.target.value)}
                         placeholder="Напротив Jade Market, серая башня" />
                </label>
                <label className="field"><span>Город</span>
                  <input value={city} onChange={(e) => setCity(e.target.value)} placeholder="Гонконг" /></label>
              </>
            )}

            <div className="field" style={{ marginBottom: 6 }}>
              <span>Валюта цен, если поставщик её не назвал</span>
            </div>
            <div className="states" style={{ marginTop: 0, marginBottom: 14 }}>
              {CURS.map((c) => (
                <button key={c} className="state-btn" aria-pressed={chosenCurrency === c}
                        onClick={() => setCurrency(c)}>{c}</button>
              ))}
            </div>

            {ready && (
              <p style={{ fontSize: 14, color: 'var(--ink-2)', margin: '0 0 12px' }}>
                Камни получат номера вида <b style={{ color: 'var(--ink)' }}>{code}-LIMING-0001</b>:
                {kind === 'show' ? ' выставка' : ' здание'}, поставщик, порядковый.
              </p>
            )}
            <div className="states" style={{ marginTop: 0 }}>
              {list.length > 0 && (
                <button className="state-btn" onClick={() => setAdding(false)}>Отмена</button>
              )}
              <button className="state-btn" aria-pressed disabled={!ready} onClick={() => void create()}>
                Завести
              </button>
            </div>
          </div>
        ) : (
          <button className="btn btn-secondary" style={{ marginTop: 14 }} onClick={() => setAdding(true)}>
            ＋ Новое место
          </button>
        )}
      </div>
    </div>
  );
}

// ── это устройство ──────────────────────────────────────────────────────────

function Device({ onBack }: { onBack: () => void }) {
  const [code, setCode] = useState('');
  const [counts, setCounts] = useState({ stones: 0, files: 0, mb: 0, queue: 0 });

  useEffect(() => {
    void (async () => {
      setCode(await deviceCode());
      const media = await db.media.toArray();
      const here = media.filter((m) => m.blob.size > 0);
      setCounts({
        stones: await db.stones.count(),
        files: here.length,
        mb: Math.round(here.reduce((a, m) => a + m.bytes, 0) / 1048576),
        queue: await db.outbox.count(),
      });
    })();
  }, []);

  return (
    <div className="app">
      <div className="bar">
        <button className="crumb" onClick={onBack}>‹ Ещё</button>
        <h1 className="t">Это устройство</h1>
      </div>

      <div className="scroll">
        <div className="attrs" style={{ marginTop: 4 }}>
          <div className="attr"><span className="k">Код</span><span className="v num">{code}</span></div>
          <div className="attr"><span className="k">Камней</span><span className="v num">{counts.stones}</span></div>
          <div className="attr"><span className="k">Файлов здесь</span><span className="v num">{counts.files} · {counts.mb} МБ</span></div>
          <div className="attr"><span className="k">В очереди</span><span className="v num">{counts.queue}</span></div>
        </div>

        <div className="memo">
          Код устройства попадает во временный номер камня, пока запись не
          доехала до сервера. Так два телефона не выдадут один и тот же номер.
        </div>

        <div className="memo">
          Снятое лежит здесь и уезжает на сервер, когда есть сеть.
          Не очищайте данные сайта в браузере, пока очередь не пуста.
        </div>
      </div>
    </div>
  );
}
